import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import {
 Alert,
 Box,
 Button,
 CircularProgress,
 Container,
 Pagination,
 PaginationItem,
 Snackbar,
 Tooltip,
 Typography,
} from "@mui/material";
import moment from "moment";
import React, { useRef, useState } from "react";
import { FormProvider } from "react-hook-form";

import useImportLeads from "./hooks/useImportLeads";
import { useImportLeadsApi } from "../../api/leadsManagement";
import SelectField from "../../components/atoms/SelectField";
import LeadsImportedGrid from "../../components/charts/LeadsImportedGrid";
import { ErrorBoundary } from "../../components/ErrorBoundary";
import { usePermissionStore } from "../../stores/permissionStore";
import { useUserStore } from "../../stores/userStore";
import ErrorBoundaryPage from "../error/ErrorBoundaryPage";
const ImportLeadsPage = () => {
 const {
  canEdit,
  leadImportData,
  formMethods,
  sourceChannelOptionsList,
  isLeadImportHistoryLoading,
  pageSize,
  currentPage,
  onPageChange,
  onRowClick,
  totalImportedLead,
  leadImportPagination,
  getLeadTemplateQuery,
  refreshHistory,
 } = useImportLeads();
 const [userState, clearUserState] = useUserStore((state) => [
  state.user,
  state.clearUser,
 ]);
 const [selectedFile, setSelectedFile] = useState<File | null>(null);
 const [selectedFileName, setSelectedFileName] = useState("");
 const [successSnackbarOpen, setSuccessSnackbarOpen] = useState(false);
 const [channelWarningSnackbarOpen, setChannelWarningSnackbarOpen] =
  useState(false);
const [importWarningSnackbarOpen, setImportWarningSnackbarOpen] =
  useState(false);
 const fileInputRef = useRef<HTMLInputElement>(null);
 const importLeadsMutation = useImportLeadsApi();
 const permissionToken = usePermissionStore.getState().permissionToken ?? "";
 const selectedChannel = formMethods.watch("channel");
 const [isUploading, setIsUploading] = useState(false);
 const uploading =
  isUploading ||
  (importLeadsMutation as any)?.isPending ||
  (importLeadsMutation as any)?.isLoading;
 const handleBrowseClick = () => {
  if (uploading) return;
  if (fileInputRef.current) {
    // Force onChange to fire even when choosing the same file
    fileInputRef.current.value = "";
    // Also reset any previous import warnings
    setImportWarningSnackbarOpen(false);
    fileInputRef.current.click();
  }
};
 const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
  if (uploading) {
    setImportWarningSnackbarOpen(false);
    return;
  }
  const file = event.target.files?.[0];
  if (file) {
    if (!file?.name?.toLowerCase().includes(selectedChannel?.toLowerCase())) {
      setImportWarningSnackbarOpen(true);

      return;
    }

   setSelectedFile(file);
   setSelectedFileName(file.name);
  }
 };
 const handleUpload = () => {
  if (!selectedFile) {
   console.error("No file selected");
   return;
  }
  if (!selectedChannel || selectedChannel === "All Channels") {
   setChannelWarningSnackbarOpen(true);
   return;
  }
  setIsUploading(true);
  importLeadsMutation.mutate(
   {
    userName: userState?.name ?? "",
    channelName: selectedChannel,
    permission_Token: permissionToken,
    file: selectedFile,
   },
   {
    onSuccess: () => {
     setSuccessSnackbarOpen(true);
     setSelectedFile(null);
     setSelectedFileName("");

     setTimeout(() => {
       refreshHistory(1, {immediate: true});
     }, 1000);
    },
    onError: (error) => {
     console.error("Upload failed:", error);
    },
    onSettled: () => {
     setIsUploading(false);
    },
   }
  );
 };
 const handleDownloadTemplate = async () => {
  if (uploading) return;
  try {
   const { data } = await getLeadTemplateQuery.refetch();
   const csvContent = typeof data === "string" ? data : data?.data;
   const fileName =
    selectedChannel && selectedChannel !== "All Channels"
     ? `${selectedChannel}_template.csv`
     : "template.csv";
   if (csvContent) {
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
   } else {
    console.error("Invalid template data format:", data);
   }
  } catch (error) {
   console.error("Failed to download template:", error);
  }
 };
 const columns = [
  { field: "channelName", headerName: "Channel", width: 120 },
  { field: "fileName", headerName: "File Name", width: 220 },
  { field: "importStatus", headerName: "Import Status", width: 140 },
  {
   field: "creationDatetime",
   headerName: "Import Date",
   width: 180,
   valueFormatter: (params) => {
    if (!params?.value) {
     return "-";
    }
    return moment.utc(params.value).local().format("YYYY-MM-DD HH:mm:ss");
   },
  },
  {
   field: "leadSyncStatus",
   headerName: "Sync Status",
   width: 140,
   valueFormatter: (params) => {
    if (!params?.value) {
     return "-";
    }
    return params.value;
   },
  },
  {
   field: "leadSyncDatetime",
   headerName: "Sync Date",
   width: 180,
   valueFormatter: (params) => {
    if (!params?.value) {
     return "-";
    }
    return moment.utc(params.value).local().format("YYYY-MM-DD HH:mm:ss");
   },
  },
  { field: "userName", headerName: "Importer Name", width: 160 },
 ];
 const isButtonsDisabled = !canEdit || uploading;
 const downloadDisabled =
  isButtonsDisabled || !selectedChannel || selectedChannel === "All Channels";
 return (
  <ErrorBoundary fallback={<ErrorBoundaryPage />}>
   <Container
    maxWidth={false}
    sx={{
     display: "flex",
     flexDirection: "column",
     width: "100%",
     marginTop: "20px",
     maxWidth: "none",
     overflow: "hidden",
     position: "relative",
    }}
   >
    <FormProvider {...formMethods}>
     <Box
      sx={{
       position: "sticky",
       top: 0,
       backgroundColor: "white",
       zIndex: 10,
       marginBottom: "16px",
       pointerEvents: uploading ? "none" : "auto",
       opacity: uploading ? 0.6 : 1,
      }}
     >
      <Box
       sx={{
        display: "grid",
        gridTemplateColumns: {
         xs: "1fr 1fr",
         md: "1fr 1fr 1fr 1fr",
        },
        gap: 1,
       }}
      >
       <Box>
        <SelectField
         options={sourceChannelOptionsList ?? []}
         name="channel"
         label="Import Channel"
         disabled={uploading}
        />
       </Box>
       <input
        type="file"
        ref={fileInputRef}
        style={{ display: "none" }}
        accept=".xls,.xlsx,.xlsm,.csv"
        onChange={handleFileChange}
        disabled={uploading}
       />
       <Button
        variant="contained"
        disabled={isButtonsDisabled}
        onClick={handleBrowseClick}
        sx={{
         height: "40px",
         backgroundColor: isButtonsDisabled ? "#ebebeb" : "#e87722",
         color: isButtonsDisabled ? "#ebebeb" : "white",
         borderRadius: "8px",
         fontWeight: 600,
         cursor: isButtonsDisabled ? "not-allowed" : "pointer",
         "&:hover": {
          backgroundColor: isButtonsDisabled ? "#ebebeb" : "#e87722",
         },
         "&:active": {
          backgroundColor: isButtonsDisabled ? "#ebebeb" : "#e87722",
         },
         "&:disabled": {
          backgroundColor: "#ebebeb",
          color: "#666666",
          border: "1px solid #00000042",
         },
        }}
       >
        Browse
       </Button>
       <Button
        variant="contained"
        disabled={isButtonsDisabled || !selectedFile}
        onClick={handleUpload}
        sx={{
         height: "40px",
         backgroundColor: isButtonsDisabled ? "#ebebeb" : "#e87722",
         color: isButtonsDisabled ? "#ebebeb" : "white",
         borderRadius: "8px",
         fontWeight: 600,
         cursor: isButtonsDisabled ? "not-allowed" : "pointer",
        }}
       >
        {selectedFileName
         ? `Upload (${selectedFileName.slice(0, 4)}... ${selectedFileName.slice(
            selectedFileName.lastIndexOf("."),
           )})`
         : "Upload"}
       </Button>
       <Tooltip
        title={downloadDisabled ? "Please select import channel" : ""}
        placement="top"
       >
        <Button
         variant="text"
         disabled={downloadDisabled}
         onClick={handleDownloadTemplate}
         sx={{
          textDecoration: downloadDisabled ? "none" : "underline",
          color: downloadDisabled ? "#999999" : "#008080",
          fontWeight: 600,
          cursor: downloadDisabled ? "not-allowed" : "pointer",
          backgroundColor: "transparent",
          padding: 0,
          minWidth: "auto",
          "&:hover": {
           textDecoration: downloadDisabled ? "none" : "underline",
           backgroundColor: "transparent",
          },
          "&:disabled": {
           color: "#999999",
          },
         }}
        >
         Download Template
        </Button>
       </Tooltip>
      </Box>
     </Box>
     <Box sx={{ flex: 1, overflow: "auto", pointerEvents: uploading ? "none" : "auto", opacity: uploading ? 0.6 : 1 }}>
      <LeadsImportedGrid
       rows={leadImportData ?? []}
       columns={columns}
       loading={isLeadImportHistoryLoading}
       pageSize={pageSize}
       currentPage={currentPage}
       onPageChange={onPageChange}
       onRowClick={onRowClick}
      />
     </Box>
     <Box
      sx={{
       display: "flex",
       justifyContent: "space-between",
       alignItems: "center",
       padding: "10px 8px",
       border: "1px solid #e0e0e0",
       borderTop: "none",
       pointerEvents: uploading ? "none" : "auto",
       opacity: uploading ? 0.6 : 1,
      }}
     >
      <Typography>{`Total ${totalImportedLead} import(s)`}</Typography>
      <Pagination
       count={leadImportPagination?.totalPages ?? 1}
       page={currentPage}
       onChange={(event, page) => onPageChange(page)}
       renderItem={(item) => (
        <PaginationItem
         slots={{ previous: ArrowBackIcon, next: ArrowForwardIcon }}
         {...item}
        />
       )}
      />
     </Box>
    </FormProvider>
    <Snackbar
     open={successSnackbarOpen}
     autoHideDuration={3000}
     onClose={() => setSuccessSnackbarOpen(false)}
     anchorOrigin={{ vertical: "top", horizontal: "right" }}
    >
     <Alert
      severity="success"
      onClose={() => setSuccessSnackbarOpen(false)}
      sx={{ minHeight: "80px", display: "flex", alignItems: "center" }}
     >
      Thanks for upload, an email will be sent to you once the process is completed.
     </Alert>
    </Snackbar>
    <Snackbar
     open={channelWarningSnackbarOpen}
     autoHideDuration={3000}
     onClose={() => setChannelWarningSnackbarOpen(false)}
     anchorOrigin={{ vertical: "top", horizontal: "right" }}
    >
     <Alert
      severity="warning"
      onClose={() => setChannelWarningSnackbarOpen(false)}
      sx={{ minHeight: "80px", display: "flex", alignItems: "center" }}
     >
      Please select a channel before uploading.
     </Alert>
    </Snackbar>
    <Snackbar
     open={importWarningSnackbarOpen}
     autoHideDuration={3000}
     onClose={() => setImportWarningSnackbarOpen(false)}
     anchorOrigin={{ vertical: "top", horizontal: "right" }}
    >
     <Alert
      severity="warning"
      onClose={() => setImportWarningSnackbarOpen(false)}
      sx={{ minHeight: "80px", display: "flex", alignItems: "center" }}
     >
      {selectedChannel === "All Channels"
        ? "Please select a channel before uploading"
        : `File name must contain channel name "${selectedChannel}".`}
     </Alert>
    </Snackbar>
    {Boolean(uploading) && (
     <Box
      sx={{
       position: "absolute",
       inset: 0,
       zIndex: (theme) => theme.zIndex.modal + 1,
       backgroundColor: "rgba(255,255,255,0.6)",
       display: "flex",
       alignItems: "center",
       justifyContent: "center",
       color: "#fff",
      }}
     >
      <CircularProgress sx={{ color: "#e87722" }} />
     </Box>
    )}
   </Container>
  </ErrorBoundary>
 );
};

export default ImportLeadsPage;
